import { Link } from "react-router-dom";
import { Check, DotsThreeVertical, TrashSimple } from "@phosphor-icons/react";
import { Pencil } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@libs/zapi-ui/shadcn";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { getRouteProjectDetails, getRouteNewVersion } from "@/zapi-frontend/shared/const/router";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { formatPortfolioDate } from "../../lib/formatPortfolioDate";
import type { TVersionHistoryItem } from "./VersionHistoryPanel";
import { useTranslation } from "react-i18next";
import { displayVersionName } from "@/zapi-frontend/shared/i18n/versionDisplayName";

type TProps = {
  version: TVersionHistoryItem;
  isCurrent: boolean;
  hasEdit: boolean;
  isActionsDisabled: boolean;
  canDelete: boolean;
  openInNewTab?: boolean;
  onSelect?: (versionId: string) => void;
  onRename: () => void;
  onDelete: () => void;
};

export function VersionRowV2(props: TProps) {
  const { t } = useTranslation("common");
  const {
    version,
    isCurrent,
    hasEdit,
    isActionsDisabled,
    canDelete,
    openInNewTab = true,
    onSelect,
    onRename,
    onDelete
  } = props;
  const showActions = hasEdit && !isActionsDisabled;
  const bodyClassName = "flex min-w-0 flex-1 flex-col gap-1 pt-1.5 text-left no-underline outline-hidden focus-visible:underline";

  const body = (
    <>
      <span className="flex h-[10px] min-w-0 items-center gap-2">
        <span
          className={cn(
            "size-1.5 shrink-0 rounded-full",
            version.isDraft ? "bg-innoveraYellow-400" : "bg-innoveraGreen-600"
          )}
        />
        <FigmaTypography component="span" variant="label-2" color="zinc900" className="min-w-0 truncate">
          {displayVersionName(version.name)}
        </FigmaTypography>
      </span>
      <span className="flex min-w-0 items-center gap-1.5">
        <FigmaTypography component="span" variant="body-2" color="zinc500" className="shrink-0 tabular-nums">
          {formatPortfolioDate(version.date)}
        </FigmaTypography>
        {version.modifiedBy ? (
          <>
            <span className="size-0.5 shrink-0 rounded-full bg-innoveraZinc-400" />
            <FigmaTypography component="span" variant="body-2" color="zinc500" className="min-w-0 truncate">
              {version.modifiedBy}
            </FigmaTypography>
          </>
        ) : null}
      </span>
    </>
  );

  return (
    <div className={cn("group/ver flex items-center gap-2 rounded-md px-2 py-2", !isActionsDisabled && "hover:bg-innoveraZinc-50")}>
      <span className="flex size-6 shrink-0 items-center justify-center">
        <Check size={16} weight="bold" className={cn("text-innoveraZinc-900", !isCurrent && "opacity-0")} />
      </span>

      {onSelect ? (
        <button
          type="button"
          className={bodyClassName}
          data-testid={`version-row-${version.id}-select-button`}
          onClick={(e) => { e.stopPropagation(); onSelect(version.id); }}
        >
          {body}
        </button>
      ) : (
        <Link
          to={version.isDraft ? getRouteNewVersion(version.id) : getRouteProjectDetails(version.id)}
          target={openInNewTab ? "_blank" : undefined}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          onClick={(e) => e.stopPropagation()}
          data-testid={`version-row-${version.id}-select-link`}
          className={bodyClassName}
        >
          {body}
        </Link>
      )}

      {showActions ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("versions.actions")}
              onClick={(e) => e.stopPropagation()}
              data-testid={`version-row-${version.id}-actions-trigger`}
              className="hidden size-8 shrink-0 items-center justify-center rounded-lg p-2 hover:bg-innoveraZinc-100 group-hover/ver:flex data-[state=open]:flex"
            >
              <DotsThreeVertical size={16} className="text-innoveraZinc-900" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[161px] space-y-1 rounded-lg border-0 p-1 shadow-300 ring-1 ring-inset ring-innoveraZinc-100"
          >
            <DropdownMenuItem
              className="h-8 gap-2 rounded-lg p-2"
              onClick={onRename}
              data-testid={`version-row-${version.id}-rename-item`}
            >
              <Pencil className="h-4 w-4 text-innoveraZinc-900" strokeWidth={1.5} />
              <FigmaTypography component="span" variant="label-2" color="zinc900">
                {t("versions.rename")}
              </FigmaTypography>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="h-8 gap-2 rounded-lg p-2"
              disabled={!canDelete}
              onClick={onDelete}
              data-testid={`version-row-${version.id}-delete-item`}
            >
              <TrashSimple size={16} className="text-innoveraRed-400" />
              <FigmaTypography component="span" variant="label-2" className="text-innoveraRed-400">
                {t("versions.delete")}
              </FigmaTypography>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
